import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiHelpCircle, FiX, FiSearch, FiPlay, FiArrowRight, FiArrowUpRight } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { SetUser } from '../../../actions/SetUser';
import { HELP_ICONS } from './helpIcons';
import { POPULAR_GUIDES, TOURS, PAGE_NAMES } from './helpContent';
import { isTourSeen, startTour } from '../tour/tourStore';
import { hrefForTitle } from './helpGuides';

interface Props {
    onClose: () => void;
}

const TEXT_SIZES = [{ value: 'standard', size: 12 }, { value: 'large', size: 14 }, { value: 'extra-large', size: 17 }];
// tours + popular guides; at least one of each always stays
const MAX_ITEMS = TOURS.length + POPULAR_GUIDES.length;
const MIN_ITEMS = 2;

// spec "Panel / Help & guides": slide-over from the right, opened by the header help button
const HelpPanel: React.FC<Props> = ({ onClose }) => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const user = useSelector((state: IGlobalState) => state.user);
    const [search, setSearch] = useState('');
    const page = PAGE_NAMES[location.pathname.split('/')[1]] ?? 'Investi';
    // "Show me around": this page's tour first (it survives when space runs out), then the rest
    const tours = [...TOURS].sort((a, b) => Number(b.match(location.pathname)) - Number(a.match(location.pathname)));
    // a tour for another page navigates there first (TourHost)
    const runTour = (id: string) => {
        onClose();
        startTour(id);
    };
    const bodyRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    // no inner scroll (user request), whatever the window height or text size: items are dropped from the bottom
    // until the content fits — popular guides first (down to 1), then the second tour — and a "More guides" link takes their place
    const [shown, setShown] = useState(MAX_ITEMS);
    const [height, setHeight] = useState(window.innerHeight);
    const toursShown = shown > TOURS.length ? TOURS.length : shown - 1;
    const guidesShown = shown > TOURS.length ? shown - TOURS.length : 1;
    const hidden = MAX_ITEMS - shown;

    useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const roomKey = useRef('');

    useLayoutEffect(() => {
        // window height or text size changed: start again from the full list (so a bigger window gets the guides back)
        const key = `${height}|${user.textSize}`;
        if (roomKey.current !== key) {
            roomKey.current = key;
            if (shown !== MAX_ITEMS) {
                setShown(MAX_ITEMS);
                return;
            }
        }
        const body = bodyRef.current;
        const content = contentRef.current;
        if (!body || !content) return;
        const style = getComputedStyle(body);
        const available = body.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        if (content.offsetHeight > available && shown > MIN_ITEMS) setShown(shown - 1);
    }, [shown, height, user.textSize]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const openCentre = () => {
        onClose();
        history.push('/help');
    };

    // applies straight away; saved with the profile only from My Profile (this panel stays UI-only)
    const setTextSize = (value: string) => dispatch(SetUser({ ...user, textSize: value }));

    return <div className="help-overlay" onClick={onClose}>
        <aside className="help-panel" role="dialog" aria-modal="true" aria-label="Help & guides" onClick={e => e.stopPropagation()}>
            <div className="panel-header">
                <div className="h">
                    <span className="icon-tile"><FiHelpCircle /></span>
                    <div className="t"><strong>Help &amp; guides</strong><span>You're on: {page}</span></div>
                    <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
                </div>
                <form className="search" onSubmit={e => { e.preventDefault(); onClose(); history.push(search.trim() ? `/help?q=${encodeURIComponent(search.trim())}` : '/help'); }}>
                    <FiSearch />
                    <input placeholder="Search help, e.g. “how do I send an announcement”" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                </form>
            </div>

            <div className="panel-body" ref={bodyRef}>
                <div className="panel-content" ref={contentRef}>
                <section>
                    <div className="section-head"><strong>About this page</strong><span>What each part of the page means</span></div>
                    <div className="tour-box">
                        <div className="tour-head">
                            <span className="icon-tile"><FiPlay /></span>
                            <div><strong>Show me around</strong><span>A short guided tour of the page you're on</span></div>
                        </div>
                        {tours.slice(0, toursShown).map(t => <button key={t.id} type="button" className="tour" onClick={() => runTour(t.id)}>
                            <FiPlay className="play" />
                            {/* "Replay" in the sub-line (the length pill is hidden on mobile) */}
                            <span className="text"><strong>{t.title}</strong><small>{[isTourSeen(user.username, t.id) && 'Replay', t.match(location.pathname) && 'This page', t.meta].filter(Boolean).join(' · ')}</small></span>
                            <span className="length">{t.length}</span>
                            <FiArrowRight />
                        </button>)}
                    </div>
                </section>

                <section>
                    <div className="section-head"><strong>Popular guides</strong></div>
                    {POPULAR_GUIDES.slice(0, guidesShown).map(g => <button key={g.title} type="button" className="guide" onClick={() => { onClose(); history.push(hrefForTitle(g.title)); }}>
                        <span className="icon-tile">{HELP_ICONS[g.icon]}</span>
                        <span className="text"><strong>{g.title}</strong><small>{g.meta}</small></span>
                        {g.video && <span className="video-badge">Video</span>}
                        <FiArrowRight className="arrow" />
                    </button>)}
                    {hidden > 0 && <button type="button" className="more-guides" onClick={openCentre}>
                        {hidden} more {hidden === 1 ? 'guide' : 'guides'} in the help centre <FiArrowRight />
                    </button>}
                </section>
                </div>
            </div>

            <div className="panel-footer">
                <span>Text size:</span>
                <div className="sizes">
                    {TEXT_SIZES.map(s => <button key={s.value} type="button" style={{ fontSize: s.size }} aria-label={`Text size ${s.value}`}
                        className={(user.textSize ?? 'standard') === s.value ? 'active' : ''} onClick={() => setTextSize(s.value)}>A</button>)}
                </div>
                <button type="button" className="open-centre" onClick={openCentre}>Open full help centre <FiArrowUpRight /></button>
            </div>
        </aside>
    </div>;
};

export default HelpPanel;
